import type {
  AssistantArtifact,
  AssistantConversation,
  AssistantConversationDetail,
  AssistantMessage,
  MapReduceProgress,
  ToolSearchResult,
  ToolTraceEntry,
} from '../../services/assistantChatService';

/** One `run_python` or `web_search` call as it happens: pending until its result arrives. */
export interface ToolActivity {
  /** The tool call's id; parallel calls of one turn are told apart by it. */
  id?: string;
  name: string;
  inputPreview: string;
  pending: boolean;
  ok?: boolean;
  stdoutPreview?: string;
  artifacts: AssistantArtifact[];
  /** A web search's sources and charge. */
  search?: ToolSearchResult;
}

export interface CompactionNotice {
  summarizedMessages: number;
}

/** A message refused for credits (a 402): the action bar offers Add Credits, and Upgrade when a bigger plan exists. */
export interface ChatBilling {
  upgradeAvailable: boolean;
}

export interface ChatState {
  conversations: AssistantConversation[];
  active: AssistantConversationDetail | null;
  loadingActive: boolean;
  streaming: boolean;
  /** The reply is saved; the stream may stay open a little longer for the reflection step. */
  replySaved: boolean;
  streamText: string;
  toolActivity: ToolActivity[];
  mapReduce: MapReduceProgress | null;
  compaction: CompactionNotice | null;
  uploading: number | null;
  error: string | null;
  /** Set with `error` when the error is a credit refusal. */
  billing: ChatBilling | null;
}

export type ChatAction =
  | { type: 'conversations'; conversations: AssistantConversation[] }
  | { type: 'active'; conversation: AssistantConversationDetail | null; loading?: boolean }
  | { type: 'uploading'; fraction: number | null }
  | { type: 'user_sent'; message: AssistantMessage }
  | { type: 'token'; text: string }
  | { type: 'tool_call'; name: string; inputPreview: string; id?: string }
  | {
    type: 'tool_result'; ok: boolean; stdoutPreview: string; artifacts: AssistantArtifact[]; search?: ToolSearchResult;
    id?: string;
  }
  | { type: 'compaction'; summarizedMessages: number; compactedThroughMessageId: string | null }
  | { type: 'mapreduce'; progress: MapReduceProgress }
  | { type: 'reply_saved'; message: AssistantMessage }
  | { type: 'stream_end' }
  | { type: 'title'; conversationId: string; title: string | null }
  | { type: 'removed'; conversationId: string }
  | { type: 'error'; message: string | null; billing?: ChatBilling | null };

export const initialChatState: ChatState = {
  conversations: [],
  active: null,
  loadingActive: false,
  streaming: false,
  replySaved: false,
  streamText: '',
  toolActivity: [],
  mapReduce: null,
  compaction: null,
  uploading: null,
  error: null,
  billing: null,
};

export const localMessage = (role: AssistantMessage['role'], content: string, status: AssistantMessage['status'] = 'complete'): AssistantMessage => ({
  id: `local-${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  content,
  model: null,
  status,
  created_at: new Date().toISOString(),
});

const withTitle = (state: ChatState, conversationId: string, title: string | null): ChatState => ({
  ...state,
  conversations: state.conversations.map((c) => (c.id === conversationId ? { ...c, title } : c)),
  active: state.active?.id === conversationId ? { ...state.active, title } : state.active,
});

const appendMessage = (state: ChatState, message: AssistantMessage): ChatState => (
  state.active ? { ...state, active: { ...state.active, messages: [...state.active.messages, message] } } : state
);

/** The tool trace persisted with a reply mirrors the live activity rows. */
export const toolTraceFromActivity = (activity: ToolActivity[]): ToolTraceEntry[] | null => (
  activity.length === 0 ? null : activity.map((row) => ({
    name: row.name,
    input_preview: row.inputPreview,
    ok: Boolean(row.ok),
    stdout_preview: row.stdoutPreview ?? '',
    artifact_ids: row.artifacts.map((artifact) => artifact.id),
    ...row.search,
  }))
);

/** Settle the row the result belongs to: by call id, else (an older server) the first one still running. */
const settleTool = (
  activity: ToolActivity[],
  id: string | undefined,
  result: { ok: boolean; stdoutPreview: string; artifacts: AssistantArtifact[]; search?: ToolSearchResult },
): ToolActivity[] => {
  const byId = id ? activity.findIndex((row) => row.id === id) : -1;
  const index = byId >= 0 ? byId : activity.findIndex((row) => row.pending);

  if (index < 0) return activity;

  return activity.map((row, position) => (position === index ? { ...row, ...result, pending: false } : row));
};

const withCompaction = (state: ChatState, summarized: number, throughId: string | null): ChatState => ({
  ...state,
  compaction: { summarizedMessages: summarized },
  active: state.active && throughId ? { ...state.active, compacted_through_message_id: throughId } : state.active,
});

const replySaved = (state: ChatState, message: AssistantMessage): ChatState => {
  const artifacts = state.toolActivity.flatMap((row) => row.artifacts);
  const saved = {
    ...message,
    tool_trace: message.tool_trace ?? toolTraceFromActivity(state.toolActivity),
    artifacts: message.artifacts?.length ? message.artifacts : artifacts,
  };

  return { ...appendMessage(state, saved), replySaved: true, streamText: '', toolActivity: [], mapReduce: null };
};

const streamReducer = (state: ChatState, action: ChatAction): ChatState | null => {
  switch (action.type) {
  case 'uploading':
    return { ...state, uploading: action.fraction };
  case 'user_sent':
    return {
      ...appendMessage(state, action.message),
      streaming: true, replySaved: false, streamText: '', toolActivity: [], mapReduce: null, compaction: null, uploading: null, error: null,
      billing: null,
    };
  case 'token':
    return { ...state, streamText: state.streamText + action.text };
  case 'tool_call':
    return {
      ...state,
      toolActivity: [
        ...state.toolActivity,
        { id: action.id, name: action.name, inputPreview: action.inputPreview, pending: true, artifacts: [] },
      ],
    };
  case 'tool_result':
    return {
      ...state,
      toolActivity: settleTool(state.toolActivity, action.id, {
        ok: action.ok, stdoutPreview: action.stdoutPreview, artifacts: action.artifacts, search: action.search,
      }),
    };
  case 'compaction':
    return withCompaction(state, action.summarizedMessages, action.compactedThroughMessageId);
  case 'mapreduce':
    return { ...state, mapReduce: action.progress };
  case 'reply_saved':
    return replySaved(state, action.message);
  case 'stream_end':
    return { ...state, streaming: false, streamText: '', toolActivity: [], mapReduce: null, uploading: null };
  default:
    return null;
  }
};

export const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  const streamed = streamReducer(state, action);

  if (streamed) return streamed;
  switch (action.type) {
  case 'conversations':
    return { ...state, conversations: action.conversations };
  case 'active':
    return {
      ...state, active: action.conversation, loadingActive: Boolean(action.loading), error: null, billing: null,
    };
  case 'title':
    return withTitle(state, action.conversationId, action.title);
  case 'removed':
    return {
      ...state,
      conversations: state.conversations.filter((c) => c.id !== action.conversationId),
      active: state.active?.id === action.conversationId ? null : state.active,
    };
  case 'error':
    return { ...state, error: action.message, billing: action.message ? action.billing ?? null : null };
  default:
    return state;
  }
};

export const conversationLabel = (conversation: { title: string | null }): string => conversation.title || 'New chat';
